import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import {
  SHIPPING_SYNC_APPLY_CAP,
  StaleCandidate,
  StaleCandidateKind,
} from "@/hooks/useShippingStaleSync";

// stale 미리보기 모달 — dumb 컴포넌트. 체크 상태·반영 실행은 컨테이너 소유.
// 그룹 순서·기본 체크 정책은 오너 결정(2026-07-29): 항목 추가만 기본 체크, 나머지 해제.

interface ShippingStaleSyncModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: StaleCandidate[];
  /** has()만 사용 — 컨테이너는 지문 Map을 쓰므로 조회 인터페이스로만 받는다 */
  checked: { has: (parsedOrderId: number) => boolean };
  onToggle: (parsedOrderId: number) => void;
  onGroupSet: (kind: StaleCandidateKind, value: boolean) => void;
  onApply: () => void;
  isApplying: boolean;
  /**
   * 스캔이 ready일 때만 반영 허용 — 표시는 마지막 확정 목록으로 고정(레이아웃 안정화)하되,
   * 재스캔 중 반영은 훅의 live 후보(비어 있음)와 어긋나므로 버튼에서 막는다.
   */
  applyReady?: boolean;
  /** yyyy-MM-dd — 스캔 범위 표기용 ("선택된 날짜"가 체크박스 선택으로 오독되는 문제 방지) */
  orderDate: string;
}

const GROUPS: { kind: StaleCandidateKind; title: string; hint: string }[] = [
  {
    // 제목은 "무슨 일이 있었나"가 아니라 "지금 뭐가 어긋나 있나"로 — 오너 피드백(2026-07-29):
    // '항목이 추가되었습니다'는 즉시 인지가 안 됨. 상품이 더 있다는 의미가 바로 읽혀야 함.
    kind: "prefix_compatible",
    title: "정산서에 상품이 더 있습니다",
    hint: "배송정보에 빠진 상품이 있습니다.\n반영하면 빠진 상품이 채워집니다.",
  },
  {
    kind: "other_difference",
    title: "정산서와 상품명이 다릅니다",
    hint: "직접 고친 상품명이 있을 수 있습니다.\n확인 후 선택합니다.",
  },
  {
    kind: "blank",
    title: "상품명 미기입",
    hint: "배송정보에 상품명이 비어 있습니다.",
  },
  {
    kind: "zero_item_with_stored",
    title: "반영 불가",
    hint: "정산서에 상품이 없어 반영할 값이 없습니다.\n배송 엑셀에는 상품명이 남아 있습니다.",
  },
];

const STATUS_BADGE: Record<string, string> = {
  conflict_stored: "다른 곳에서 수정됨",
  conflict_items: "주문이 변경됨",
  missing: "행 없음",
  empty: "값 없음",
  invalid: "형식 오류",
};

export function ShippingStaleSyncModal({
  open,
  onOpenChange,
  candidates,
  checked,
  onToggle,
  onGroupSet,
  onApply,
  isApplying,
  applyReady = true,
  orderDate,
}: ShippingStaleSyncModalProps) {
  const checkedEligible = candidates.filter(
    (c) => c.applyEligible && checked.has(c.parsedOrderId)
  ).length;
  const overCap = checkedEligible > SHIPPING_SYNC_APPLY_CAP;
  const dateLabel = (() => {
    try {
      return format(parseISO(orderDate), "M월 d일");
    } catch {
      return orderDate;
    }
  })();

  return (
    <Dialog open={open} onOpenChange={(next) => !isApplying && onOpenChange(next)}>
      {/* 반영 중엔 기본 X 버튼을 아예 숨김 — pointer-events만 끊으면 키보드 포커스와
          보조기기 노출이 남아 "눌리는데 무시되는" 버튼이 된다(접근성 지적). */}
      <DialogContent
        className={`max-w-2xl max-h-[85vh] flex flex-col ${
          isApplying ? "[&>button.absolute]:hidden" : ""
        }`}
      >
        <DialogHeader>
          <DialogTitle>정산서와 다른 배송정보</DialogTitle>
          {/* 핵심 설명은 콜아웃으로 승격 — 회색 보조 텍스트로는 인지가 안 된다는 오너 피드백.
              배너와 같은 앰버 계열 = 같은 흐름이라는 시각 신호. a11y 설명 연결은 유지(asChild). */}
          <DialogDescription asChild>
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-left">
              <p className="text-sm font-semibold text-amber-900">
                선택한 배송정보의 상품명을 현재 정산서 기준으로 바꿉니다.
              </p>
              <p className="mt-0.5 text-xs text-amber-800/70">
                지금 보고 있는 {dateLabel} 배송정보만 확인합니다.
                <br />
                다른 날짜는 날짜를 이동하면 자동으로 확인됩니다.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* 그룹은 접힌 상태로 시작(오너 피드백 2026-07-29 — 한 번에 다 펼치면 인지부하).
            접힌 상태에서도 판단 가능하도록 트리거에 건수·선택수 요약을 노출하고,
            기본 체크는 접혀 있어도 이미 적용돼 있다(푸터의 반영 카운트로 확인 가능). */}
        <div className="flex-1 overflow-y-auto pr-1">
          <Accordion type="multiple" className="w-full">
          {GROUPS.map(({ kind, title, hint }) => {
            const group = candidates.filter((c) => c.kind === kind);
            if (group.length === 0) return null;
            const selectable = kind !== "zero_item_with_stored";
            const eligible = group.filter((c) => c.applyEligible);
            const checkedInGroup = eligible.filter((c) => checked.has(c.parsedOrderId)).length;
            const allChecked =
              selectable && eligible.length > 0 && checkedInGroup === eligible.length;
            return (
              <AccordionItem key={kind} value={kind}>
                <AccordionTrigger className="py-3 hover:no-underline">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-gray-800">{title}</span>
                    <span className="text-xs text-gray-500 shrink-0">배송정보 {group.length}건</span>
                    {selectable && checkedInGroup > 0 && (
                      // Badge(div)는 button>span 안에서 invalid HTML — 트리거 안은 span으로만
                      <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold px-2 py-0.5 shrink-0">
                        {checkedInGroup}건 선택됨
                      </span>
                    )}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-gray-400 whitespace-pre-line">{hint}</p>
                  {selectable && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 shrink-0"
                      disabled={isApplying || !applyReady || eligible.length === 0}
                      aria-label={`${title} 그룹 ${allChecked ? "모두 해제" : "모두 선택"}`}
                      onClick={() => onGroupSet(kind, !allChecked)}
                    >
                      {allChecked ? "모두 해제" : "모두 선택"}
                    </Button>
                  )}
                </div>
                <ul className="space-y-2">
                  {group.map((c) => (
                    <li
                      key={c.parsedOrderId}
                      className="flex items-start gap-3 rounded-md border border-gray-200 px-3 py-2"
                    >
                      {selectable ? (
                        <Checkbox
                          className="mt-0.5"
                          checked={checked.has(c.parsedOrderId)}
                          disabled={!c.applyEligible || isApplying || !applyReady}
                          aria-label={`${c.nickname || "닉네임 없음"} 배송정보 반영 선택`}
                          onCheckedChange={() => onToggle(c.parsedOrderId)}
                        />
                      ) : (
                        <span className="w-4" />
                      )}
                      <div className="min-w-0 flex-1 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-800 truncate">
                            {c.nickname || "(닉네임 없음)"}
                          </span>
                          {c.duplicateOrderId && (
                            // 스캔 범위(선택 날짜) 안의 중복만 감지한다 — 다른 날짜의 형제 행은
                            // 대상 밖(문서화된 한계). 문구도 그 범위로 한정.
                            <Badge variant="outline" className="text-[10px] text-gray-500">
                              이 날짜에 같은 주문의 배송정보 행 여러 개
                            </Badge>
                          )}
                          {!c.applyEligible && kind !== "zero_item_with_stored" && (
                            <Badge variant="outline" className="text-[10px] text-gray-500">
                              반영 불가
                            </Badge>
                          )}
                          {c.applyStatus && c.applyStatus !== "applied" && (
                            <Badge className="text-[10px] bg-red-100 text-red-700 hover:bg-red-100">
                              {STATUS_BADGE[c.applyStatus] ?? c.applyStatus}
                            </Badge>
                          )}
                        </div>
                        <div className="mt-1 grid gap-0.5 text-xs">
                          <div className="text-gray-500 break-all">
                            <span className="inline-block w-8 shrink-0 text-gray-400">현재</span>
                            {c.stored && c.stored.trim() ? c.stored : "(비어 있음)"}
                          </div>
                          {c.kind !== "zero_item_with_stored" && (
                            <div className="text-gray-800 break-all">
                              <span className="inline-block w-8 shrink-0 text-gray-400">변경</span>
                              {c.synthesized}
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                </AccordionContent>
              </AccordionItem>
            );
          })}
          </Accordion>
          {candidates.length === 0 && (
            <p className="text-sm text-gray-500 py-6 text-center">
              정산서와 다른 배송정보가 없습니다.
            </p>
          )}
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 sm:flex-col">
          {overCap && (
            <p className="text-xs text-red-600">
              한 번에 반영 가능한 건수는 {SHIPPING_SYNC_APPLY_CAP}건입니다.
              <br />
              선택을 줄인 후 반영이 가능합니다.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              disabled={isApplying}
              onClick={() => onOpenChange(false)}
            >
              닫기
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              disabled={checkedEligible === 0 || overCap || isApplying || !applyReady}
              onClick={onApply}
            >
              {isApplying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  반영 중
                </>
              ) : (
                `선택한 배송정보 ${checkedEligible}건 반영`
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
